import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Canvas, useFrame } from '@react-three/fiber';
import { Points, PointMaterial } from '@react-three/drei';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import * as random from 'maath/random/dist/maath-random.esm';

const {
  FiArrowRight, FiPhone, FiCalendar,
  FiChevronLeft, FiChevronRight,
  FiZap, FiCamera, FiHome, FiSun, FiTool
} = FiIcons;

// ─────────────────────────────────────────────
// 🖼️  SLIDES — swap image paths with your own
// ─────────────────────────────────────────────
const SLIDES = [
  {
    id: 1,
    image: '/images/hero/electrical-installation.jpg',
    badge: 'Expert Electrical Services',
    headline: ['Powering Every', 'Home & Business'],
    sub: 'Complete electrical installation, wiring & maintenance with 13+ years of trusted expertise across Kashmir.',
    icon: FiZap,
    highlight: 'Licensed & Certified Electricians',
    accent: '#38bdf8',
  },
  {
    id: 2,
    image: '/images/hero/solar-power.jpg',
    badge: 'Solar Energy Solutions',
    headline: ['Harness the Sun,', 'Cut Your Bills'],
    sub: "Premium solar panel installation & maintenance — clean, renewable energy tailored for Kashmir's climate.",
    icon: FiSun,
    highlight: 'Up to 80% Energy Savings',
    accent: '#fbbf24',
  },
  {
    id: 3,
    image: '/images/hero/cctv-security.jpg',
    badge: 'Advanced Security Systems',
    headline: ['See Everything,', 'Miss Nothing'],
    sub: 'State-of-the-art CCTV and security systems keeping your family and assets protected around the clock.',
    icon: FiCamera,
    highlight: '24/7 HD Surveillance Coverage',
    accent: '#a78bfa',
  },
  {
    id: 4,
    image: '/images/hero/smart-home.jpg',
    badge: 'Smart Home Automation',
    headline: ['Your Home,', 'Intelligently Controlled'],
    sub: 'Transform your living space with cutting-edge automation — lighting, climate & security at your fingertips.',
    icon: FiHome,
    highlight: 'Voice & App Controlled Systems',
    accent: '#2dd4bf',
  },
  {
    id: 5,
    image: '/images/hero/annual-maintenance.jpg',
    badge: 'Annual Maintenance Contracts',
    headline: ['Worry-Free Maintenance,', 'Year Round'],
    sub: 'Comprehensive AMC plans keeping all your electrical and security systems running at peak performance.',
    icon: FiTool,
    highlight: 'Priority Response Guaranteed',
    accent: '#34d399',
  },
];

const AUTOPLAY_MS = 5500;

// ─────────────────────────────────────────────
// 3-D Star field (unchanged from your original)
// ─────────────────────────────────────────────
function Stars() {
  const ref = useRef();
  const sphere = random.inSphere(new Float32Array(5000), { radius: 1.5 });
  useFrame((_, delta) => {
    ref.current.rotation.x -= delta / 10;
    ref.current.rotation.y -= delta / 15;
  });
  return (
    <group rotation={[0, 0, Math.PI / 4]}>
      <Points ref={ref} positions={sphere} stride={3} frustumCulled={false}>
        <PointMaterial transparent color="#0066ff" size={0.002} sizeAttenuation depthWrite={false} />
      </Points>
    </group>
  );
}

// ─────────────────────────────────────────────
// Electric canvas (unchanged from your original)
// ─────────────────────────────────────────────
function ElectricCanvas({ mousePos }) {
  const canvasRef = useRef(null);
  const frameRef  = useRef(0);
  const arcsRef   = useRef([]);
  const orbsRef   = useRef([]);

  useEffect(() => {
    orbsRef.current = Array.from({ length: 6 }, (_, i) => ({
      x: Math.random(), y: Math.random(),
      vx: (Math.random() - 0.5) * 0.0003,
      vy: (Math.random() - 0.5) * 0.0003,
      radius: 120 + Math.random() * 180,
      opacity: 0.06 + Math.random() * 0.08,
      color: i % 2 === 0 ? '0,102,255' : '0,204,255',
      phase: Math.random() * Math.PI * 2,
    }));
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;

    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener('resize', resize);

    const spawnArc = () => {
      const w = canvas.width, h = canvas.height;
      const startX = Math.random() * w;
      const startY = Math.random() * h * 0.6;
      const len    = 60 + Math.random() * 140;
      const angle  = (Math.random() - 0.5) * Math.PI;
      arcsRef.current.push({
        x: startX, y: startY,
        endX: startX + Math.cos(angle) * len,
        endY: startY + Math.sin(angle) * len,
        segments: Array.from({ length: 8 }, () => ({ ox: (Math.random() - 0.5) * 18, oy: (Math.random() - 0.5) * 18 })),
        life: 0, maxLife: 18 + Math.random() * 14,
        color: Math.random() > 0.5 ? '0,180,255' : '250,204,21',
        width: 0.4 + Math.random() * 0.8,
      });
    };

    const draw = () => {
      const w = canvas.width, h = canvas.height;
      ctx.clearRect(0, 0, w, h);
      const gridSize = 55, cols = Math.ceil(w / gridSize) + 1, rows = Math.ceil(h / gridSize) + 1;
      const t  = frameRef.current * 0.012;
      const mx = mousePos.current.x * w, my = mousePos.current.y * h;

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const gx = c * gridSize, gy = r * gridSize;
          const dist = Math.hypot(gx - mx, gy - my);
          const mouseInfluence = Math.max(0, 1 - dist / 280);
          const wave  = Math.sin(t + c * 0.4 + r * 0.3) * 0.5 + 0.5;
          const alpha = 0.06 + wave * 0.08 + mouseInfluence * 0.25;
          const size  = 1 + wave * 0.8 + mouseInfluence * 2;
          ctx.beginPath(); ctx.arc(gx, gy, size, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(0,102,255,${alpha})`; ctx.fill();
        }
      }

      const scanY = (t * 60) % h;
      const scanGrad = ctx.createLinearGradient(0, scanY - 60, 0, scanY + 60);
      scanGrad.addColorStop(0, 'rgba(0,102,255,0)');
      scanGrad.addColorStop(0.5, 'rgba(0,153,255,0.04)');
      scanGrad.addColorStop(1, 'rgba(0,102,255,0)');
      ctx.fillStyle = scanGrad; ctx.fillRect(0, scanY - 60, w, 120);

      orbsRef.current.forEach(orb => {
        orb.x += orb.vx; orb.y += orb.vy;
        if (orb.x < 0 || orb.x > 1) orb.vx *= -1;
        if (orb.y < 0 || orb.y > 1) orb.vy *= -1;
        orb.phase += 0.015;
        const pulse = Math.sin(orb.phase) * 0.3 + 0.7;
        const grad  = ctx.createRadialGradient(orb.x * w, orb.y * h, 0, orb.x * w, orb.y * h, orb.radius * pulse);
        grad.addColorStop(0, `rgba(${orb.color},${orb.opacity * pulse})`);
        grad.addColorStop(0.5, `rgba(${orb.color},${orb.opacity * 0.3 * pulse})`);
        grad.addColorStop(1, `rgba(${orb.color},0)`);
        ctx.fillStyle = grad; ctx.beginPath(); ctx.arc(orb.x * w, orb.y * h, orb.radius * pulse, 0, Math.PI * 2); ctx.fill();
      });

      if (mx > 0 && my > 0) {
        const mGrad = ctx.createRadialGradient(mx, my, 0, mx, my, 200);
        mGrad.addColorStop(0, 'rgba(0,153,255,0.08)');
        mGrad.addColorStop(0.5, 'rgba(0,102,255,0.03)');
        mGrad.addColorStop(1, 'rgba(0,102,255,0)');
        ctx.fillStyle = mGrad; ctx.beginPath(); ctx.arc(mx, my, 200, 0, Math.PI * 2); ctx.fill();
      }

      if (frameRef.current % 12 === 0 && Math.random() > 0.3) spawnArc();
      arcsRef.current = arcsRef.current.filter(arc => arc.life < arc.maxLife);
      arcsRef.current.forEach(arc => {
        arc.life++;
        const progress = arc.life / arc.maxLife;
        const alpha    = Math.sin(progress * Math.PI) * 0.7;
        ctx.save();
        ctx.strokeStyle = `rgba(${arc.color},${alpha})`;
        ctx.lineWidth   = arc.width;
        ctx.shadowBlur  = 6;
        ctx.shadowColor = `rgba(${arc.color},0.8)`;
        ctx.beginPath(); ctx.moveTo(arc.x, arc.y);
        arc.segments.forEach((seg, i) => {
          const t2 = (i + 1) / arc.segments.length;
          ctx.lineTo(
            arc.x + (arc.endX - arc.x) * t2 + seg.ox * Math.sin(progress * Math.PI),
            arc.y + (arc.endY - arc.y) * t2 + seg.oy * Math.sin(progress * Math.PI)
          );
        });
        ctx.lineTo(arc.endX, arc.endY); ctx.stroke(); ctx.restore();
      });

      const cornerLen = 80;
      const corners   = [
        { x: 0, y: 0, dx: 1, dy: 1 }, { x: w, y: 0, dx: -1, dy: 1 },
        { x: 0, y: h, dx: 1, dy: -1 }, { x: w, y: h, dx: -1, dy: -1 },
      ];
      const cornerAlpha = 0.15 + Math.sin(t * 2) * 0.08;
      corners.forEach(({ x, y, dx, dy }) => {
        ctx.save(); ctx.strokeStyle = `rgba(0,102,255,${cornerAlpha})`; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + dx * cornerLen, y); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x, y + dy * cornerLen); ctx.stroke();
        ctx.restore();
      });

      frameRef.current++;
      animId = requestAnimationFrame(draw);
    };

    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);

  return (
    <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none', zIndex: 2 }} />
  );
}

// ─────────────────────────────────────────────
// Slide progress bar
// ─────────────────────────────────────────────
const ProgressBar = ({ active, accent }) => (
  <div className="h-[2px] bg-white/20 rounded-full overflow-hidden flex-1">
    {active
      ? <motion.div className="h-full rounded-full" style={{ background: accent }}
          initial={{ width: '0%' }} animate={{ width: '100%' }}
          transition={{ duration: AUTOPLAY_MS / 1000, ease: 'linear' }} key={Date.now()} />
      : <div className="h-full w-full bg-white/30 rounded-full" />}
  </div>
);

// ─────────────────────────────────────────────
// Main Hero
// ─────────────────────────────────────────────
const Hero = () => {
  const mousePos = useRef({ x: 0.5, y: 0.5 });
  const [current, setCurrent] = useState(0);
  const [paused,  setPaused]  = useState(false);

  const next = useCallback(() => setCurrent(p => (p + 1) % SLIDES.length), []);
  const prev = useCallback(() => setCurrent(p => (p - 1 + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    if (paused) return;
    const t = setTimeout(next, AUTOPLAY_MS);
    return () => clearTimeout(t);
  }, [current, paused, next]);

  const slide = SLIDES[current];

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mousePos.current = {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    };
  };

  return (
    <section
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* ── Layer 1: 3-D stars ── */}
      <div className="absolute inset-0 z-0">
        <Canvas camera={{ position: [0, 0, 1] }}>
          <Stars />
        </Canvas>
      </div>

      {/* ── Layer 2: Electric canvas ── */}
      <ElectricCanvas mousePos={mousePos} />

      {/* ── Layer 3: Slideshow background image ── */}
      <AnimatePresence mode="wait">
        <motion.div
          key={slide.id}
          className="absolute inset-0 z-[3]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1, ease: 'easeInOut' }}
        >
          <img
            src={slide.image}
            alt={slide.badge}
            className="w-full h-full object-cover object-center"
            loading="eager"
          />
          {/* Heavy dark overlay so the electric canvas still bleeds through */}
          <div className="absolute inset-0 bg-black/65" />
          {/* Left-side accent tint matching slide colour */}
          <div
            className="absolute inset-y-0 left-0 w-2/3"
            style={{ background: `linear-gradient(to right, ${slide.accent}18, transparent)` }}
          />
        </motion.div>
      </AnimatePresence>

      {/* ── Layer 4: Main gradient overlay ── */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/40 to-blue-900/10 z-[4]" />

      {/* ── Layer 5: Floating lightning bolts (unchanged) ── */}
      <div className="absolute inset-0 z-[5] pointer-events-none">
        {[...Array(8)].map((_, i) => (
          <motion.div key={i} className="absolute"
            style={{ left: `${10 + i * 11}%`, top: `${15 + (i % 4) * 20}%` }}
            animate={{ opacity: [0, 0.6, 0], y: [0, -25, 0], scale: [0.6, 1, 0.6] }}
            transition={{ duration: 2 + Math.random(), repeat: Infinity, delay: i * 0.5, ease: 'easeInOut' }}>
            <svg width="10" height="16" viewBox="0 0 10 16" fill="none">
              <path d="M6 0L0 9h4L4 16l6-9H6V0z" fill="#facc15" opacity="0.7" />
            </svg>
          </motion.div>
        ))}
      </div>

      {/* ── Layer 6: Slide content ── */}
      <div className="relative z-[6] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-24 pb-28 flex items-center min-h-screen">
        <AnimatePresence mode="wait">
          <motion.div
            key={slide.id + '-content'}
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -18 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="max-w-2xl"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 }}
              className="inline-flex items-center space-x-2 mb-5 px-3 py-1.5 rounded-full border"
              style={{ background: slide.accent + '18', borderColor: slide.accent + '50' }}
            >
              <SafeIcon icon={slide.icon} style={{ color: slide.accent }} className="text-sm" />
              <span className="text-xs font-bold tracking-[0.18em] uppercase" style={{ color: slide.accent }}>
                {slide.badge}
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-5"
            >
              <span className="gradient-text">{slide.headline[0]}</span>
              <br />
              <span className="text-white">{slide.headline[1]}</span>
            </motion.h1>

            {/* Sub */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.35 }}
              className="text-gray-300 text-base sm:text-lg leading-relaxed mb-5 max-w-lg"
            >
              {slide.sub}
            </motion.p>

            {/* Highlight pill */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.42 }}
              className="inline-flex items-center space-x-2 mb-8 px-3 py-1.5 rounded-full"
              style={{ background: slide.accent + '15', border: `1px solid ${slide.accent}35` }}
            >
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: slide.accent }} />
              <span className="text-xs font-semibold" style={{ color: slide.accent }}>{slide.highlight}</span>
            </motion.div>

            {/* CTA buttons */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4"
            >
              <Link
                to="/appointments"
                className="group inline-flex items-center justify-center space-x-2 px-6 py-3 rounded-full text-white font-semibold text-sm sm:text-base transition-all duration-300 hover:scale-105"
                style={{
                  background: `linear-gradient(135deg, ${slide.accent}, #2563eb)`,
                  boxShadow: `0 0 22px ${slide.accent}44`,
                }}
              >
                <SafeIcon icon={FiCalendar} className="text-sm" />
                <span>Book Appointment</span>
              </Link>

              <a
                href="https://wa.me/919906669918"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center space-x-2 px-6 py-3 rounded-full font-semibold text-sm sm:text-base border border-white/25 text-white hover:bg-white/10 hover:border-white/50 transition-all duration-300"
              >
                <SafeIcon icon={FiPhone} className="text-sm" />
                <span>Talk on WhatsApp</span>
              </a>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ── Arrow controls ── */}
      <button onClick={prev}
        className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 z-[7] w-9 h-9 rounded-full bg-black/40 border border-white/20 flex items-center justify-center text-white hover:bg-white/15 transition-all duration-200 backdrop-blur-sm"
        aria-label="Previous slide">
        <SafeIcon icon={FiChevronLeft} className="text-base" />
      </button>
      <button onClick={next}
        className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 z-[7] w-9 h-9 rounded-full bg-black/40 border border-white/20 flex items-center justify-center text-white hover:bg-white/15 transition-all duration-200 backdrop-blur-sm"
        aria-label="Next slide">
        <SafeIcon icon={FiChevronRight} className="text-base" />
      </button>

      {/* ── Bottom: progress bars + counter ── */}
      <div className="absolute bottom-8 left-4 sm:left-8 right-4 sm:right-8 z-[7]">
        <div className="flex items-center gap-2 mb-2">
          {SLIDES.map((s, i) => (
            <button key={s.id} onClick={() => setCurrent(i)} className="flex-1" aria-label={`Slide ${i + 1}`}>
              <ProgressBar active={i === current} accent={slide.accent} />
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <span className="text-white/40 text-[10px] font-mono tracking-widest">
            {String(current + 1).padStart(2, '0')} / {String(SLIDES.length).padStart(2, '0')}
          </span>
          <span className="text-white/30 text-[10px] hidden sm:block">
            {paused ? '⏸ Paused' : '▶ Auto-playing'}
          </span>
        </div>
      </div>

      {/* ── Floating pulse dots (unchanged) ── */}
      <div className="absolute inset-0 z-[5] pointer-events-none">
        <div className="absolute top-1/4 left-10 w-4 h-4 bg-blue-400 rounded-full animate-pulse opacity-60" />
        <div className="absolute top-1/3 right-16 w-3 h-3 bg-cyan-400 rounded-full animate-pulse opacity-40" />
        <div className="absolute bottom-1/4 left-1/4 w-2 h-2 bg-blue-300 rounded-full animate-pulse opacity-50" />
        <div className="absolute bottom-1/3 right-1/3 w-5 h-5 bg-blue-500 rounded-full animate-pulse opacity-30" />
      </div>

      {/* ── Bottom fade ── */}
      <div className="absolute bottom-0 left-0 right-0 h-32 z-[6] pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, transparent, #000000)' }} />
    </section>
  );
};

export default Hero;